import React, { useState, useCallback, useMemo } from 'react';
import { Tabs } from 'antd';
import { Box } from 'atoms';
import { useQueryParams } from 'hooks';
import {
	RequestQueueTab,
	RequestHistoryTab,
	FinanceRequestFilters,
	StatusFilterBar,
} from './components';
import { fetchRequestQueue, fetchRequestHistory } from 'store/financeSlice';
import { fetchRequestQueueV2, fetchRequestHistoryV2 } from 'store/financeSliceV2';
import { getUIVersion } from 'utils/router-static-data';

const { TabPane } = Tabs;

const RequestQueue = 'request-queue';
const RequestHistory = 'request-history';

function RequestsPage() {
	const [activeTab, setActiveTab] = useState(RequestQueue);
	const isV2 = useMemo(() => getUIVersion() === 'new', []);

	// State for each tab
	const [requestQueueData, setRequestQueueData] = useState({ rows: [], count: 0 });
	const [requestHistoryData, setRequestHistoryData] = useState({
		rows: [],
		count: 0,
		marked_count: 0,
		unmarked_count: 0,
	});
	const [selectedRequestIds, setSelectedRequestIds] = useState([]);

	// Pagination config for each tab using useQueryParams
	const [requestQueuePagination, setRequestQueuePagination] = useQueryParams({
		search_type: 'finance_request',
		fetch_filters: false,
		business_unit_ids: [],
		persistKey: 'financeRequestQueue:filters',
	});

	const [requestHistoryPagination, setRequestHistoryPagination] = useQueryParams({
		search_type: 'finance_request_history',
		fetch_filters: false,
		business_unit_ids: [],
		persistKey: 'financeRequestHistory:filters',
	});

	// Helper function to handle business unit IDs for each tab
	const getBusinessUnitIds = useCallback((paginationConfig) => {
		const buIds = paginationConfig?.business_unit_ids;
		let result = [];

		if (Array.isArray(buIds)) {
			result = buIds;
		} else if (buIds) {
			result = [buIds];
		}

		return result.map((businessUnitId) => String(businessUnitId).trim()).filter(Boolean);
	}, []);

	// Dispatch actions for each tab
	const memoizedRequestQueueAction = useCallback(
		(params) => {
			const currentConfig = requestQueuePagination;
			const filterParams = {
				limit: params.limit || 10,
				offset: params.offset || 0,
				query_str: currentConfig?.query_str,
				city: currentConfig?.city,
				client_id: currentConfig?.client_id,
				project_id: currentConfig?.project_id,
				vendor_id: currentConfig?.vendor_id,
				business_unit_ids: getBusinessUnitIds(currentConfig),
				from_date: currentConfig?.from_date,
				to_date: currentConfig?.to_date,
			};
			const action = isV2
				? fetchRequestQueueV2(filterParams)
				: fetchRequestQueue(filterParams);
			return async (dispatchThunk) =>
				dispatchThunk(action).then((result) => ({
					rows: result.rows || [],
					count: result.count || 0,
				}));
		},
		[requestQueuePagination, getBusinessUnitIds, isV2]
	);

	const memoizedRequestHistoryAction = useCallback(
		(params) => {
			const currentConfig = requestHistoryPagination;
			const filterParams = {
				limit: params.limit || 10,
				offset: params.offset || 0,
				query_str: currentConfig?.query_str,
				city: currentConfig?.city,
				client_id: currentConfig?.client_id,
				project_id: currentConfig?.project_id,
				vendor_id: currentConfig?.vendor_id,
				business_unit_ids: getBusinessUnitIds(currentConfig),
				from_date: currentConfig?.from_date,
				to_date: currentConfig?.to_date,
				unmarked_only: currentConfig?.unmarked_only || false,
			};
			if (isV2) {
				return async (dispatchThunk) =>
					dispatchThunk(fetchRequestHistoryV2(filterParams)).then((result) => ({
						rows: result.rows || [],
						count: result.count || 0,
						marked_count: result.marked_count || 0,
						unmarked_count: result.unmarked_count || 0,
					}));
			}
			return async (dispatchThunk) =>
				dispatchThunk(fetchRequestHistory(filterParams)).then((result) => ({
					rows: result?.rows?.data || [],
					count: result?.count || 0,
					marked_count: result?.rows?.marked_count || 0,
					unmarked_count: result?.rows?.unmarked_count || 0,
				}));
		},
		[requestHistoryPagination, getBusinessUnitIds, isV2]
	);

	const handleUnmarkedOnlyToggle = useCallback(
		(checked) => {
			setRequestHistoryPagination((prev) => ({
				...prev,
				unmarked_only: checked,
				offset: 0,
				currentPage: 1,
			}));
		},
		[setRequestHistoryPagination]
	);

	return (
		<Box mt={4}>
			<Tabs activeKey={activeTab} onChange={setActiveTab}>
				<TabPane tab="Request Queue" key={RequestQueue}>
					<FinanceRequestFilters
						paginationConfig={requestQueuePagination}
						setPaginationConfig={setRequestQueuePagination}
						filterKey="REQUEST_QUEUE"
						selectedRequestIds={selectedRequestIds}
						onMarkToPay={() => {
							// Refresh table after update by resetting pagination
							setSelectedRequestIds([]);
							setRequestQueuePagination((prev) => ({
								...prev,
								offset: 0,
								currentPage: 1,
							}));
						}}
					/>
					<RequestQueueTab
						tableDatas={[requestQueueData, setRequestQueueData]}
						togglePaginate={[requestQueuePagination, setRequestQueuePagination]}
						dispatchAction={memoizedRequestQueueAction}
						onSelectionChange={setSelectedRequestIds}
					/>
				</TabPane>
				<TabPane tab="History" key={RequestHistory}>
					<FinanceRequestFilters
						paginationConfig={requestHistoryPagination}
						setPaginationConfig={setRequestHistoryPagination}
						filterKey="REQUEST_HISTORY"
					/>
					<StatusFilterBar
						markedCount={requestHistoryData.marked_count}
						unmarkedCount={requestHistoryData.unmarked_count}
						unmarkedOnly={requestHistoryPagination?.unmarked_only || false}
						onToggle={handleUnmarkedOnlyToggle}
					/>
					<RequestHistoryTab
						tableDatas={[requestHistoryData, setRequestHistoryData]}
						togglePaginate={[requestHistoryPagination, setRequestHistoryPagination]}
						dispatchAction={memoizedRequestHistoryAction}
					/>
				</TabPane>
			</Tabs>
		</Box>
	);
}

export default RequestsPage;
